// データテーブルのレポート追加モーダルコンポーネント

import { useState, useMemo } from 'react';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import { Report } from '../../types/report.types';
import { DataSet } from '../../types';
import { ROW_ID_COLUMN_ID } from '../../types/system-columns';
import * as styles from '../styles/components/data-table-report-modal.css.ts';
import logger from '../utils/logger';

interface DataTableReportModalProps {
  reports: Report[];
  dataset: DataSet;
  onConfirm: (
    reportId: string | null, 
    newReportName: string, 
    newReportDescription: string,
    displayColumns: string[],
    maxRows: number
  ) => void;
  onClose: () => void;
}

export function DataTableReportModal({
  reports,
  dataset,
  onConfirm,
  onClose
}: DataTableReportModalProps) {
  // RowNumber列を特定
  const rowNumberColumn = dataset.schema.columns.find(col => col.id === ROW_ID_COLUMN_ID)?.name;
  
  // データセットの列を取得（RowNumber列は除外 - UIには表示しない）
  const availableColumns = useMemo(() => {
    return dataset.schema.columns
      .filter(col => col.id !== ROW_ID_COLUMN_ID)
      .map(col => col.name);
  }, [dataset.schema.columns]);
  
  // 初期値を直接計算（モーダルが開くたびに新しい値）
  const initialMode = reports.length > 0 ? 'select' : 'create';
  const initialReportId = reports.length > 0 ? reports[0].id : null;
  // 最初の5列を選択（RowNumber列は別途自動追加）
  const initialColumns = availableColumns.slice(0, 5);

  // 状態管理（初期値付き）
  const [mode, setMode] = useState<'select' | 'create'>(initialMode);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(initialReportId);
  const [newReportName, setNewReportName] = useState('');
  const [newReportDescription, setNewReportDescription] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialColumns);
  const [maxRows, setMaxRows] = useState(100);

  const handleColumnToggle = (columnName: string) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnName)) {
        return prev.filter(col => col !== columnName);
      } else {
        return [...prev, columnName];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns(availableColumns);
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleConfirm = () => {
    if (selectedColumns.length === 0) {
      logger.warn('[DataTableReportModal] No columns selected');
      return;
    }

    if (mode === 'select' && !selectedReportId) {
      logger.warn('[DataTableReportModal] No report selected');
      return;
    }

    if (mode === 'create' && !newReportName.trim()) {
      logger.warn('[DataTableReportModal] No report name provided');
      return;
    }

    // RowNumber列を自動的に最初に追加
    const finalColumns = rowNumberColumn 
      ? [rowNumberColumn, ...selectedColumns]
      : selectedColumns;

    onConfirm(
      mode === 'select' ? selectedReportId : null,
      newReportName,
      newReportDescription,
      finalColumns,
      maxRows
    );
  };

  // 常にレンダリング（親コンポーネントが表示を制御）
  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title="Add Data Table to Report"
      width="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={
              selectedColumns.length === 0 ||
              (mode === 'select' && !selectedReportId) ||
              (mode === 'create' && !newReportName.trim())
            }
          >
            {mode === 'select' ? 'Add to Selected Report' : 'Create and Add'}
          </Button>
        </>
      }
    >
      <div>
        {/* タブ切り替え（レポートが存在する場合のみ表示） */}
        {reports.length > 0 && (
          <div className={styles.modalTabs}>
            <button
              className={`${styles.modalTab} ${mode === 'select' ? styles.active : ''}`}
              onClick={() => setMode('select')}
            >
              Select Existing Report
            </button>
            <button
              className={`${styles.modalTab} ${mode === 'create' ? styles.active : ''}`}
              onClick={() => setMode('create')}
            >
              Create New Report
            </button>
          </div>
        )}

        {/* 既存レポート選択モード */}
        {mode === 'select' && (
          <div className={styles.reportList}>
            {reports.map(report => (
              <label key={report.id} className={styles.reportItem}>
                <input
                  type="radio"
                  name="report"
                  value={report.id}
                  checked={selectedReportId === report.id}
                  onChange={(e) => setSelectedReportId(e.target.value)}
                />
                <div className={styles.reportInfo}>
                  <div className={styles.reportName}>{report.name}</div>
                  {report.description && (
                    <div className={styles.reportDescription}>{report.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {/* 新規レポート作成モード */}
        {mode === 'create' && (
          <div className={styles.createReportForm}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Report Name</label>
              <input
                type="text"
                className={styles.formInput}
                value={newReportName}
                onChange={(e) => setNewReportName(e.target.value)}
                placeholder="Enter report name"
                autoFocus
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Description (Optional)</label>
              <textarea
                className={styles.formTextarea}
                value={newReportDescription}
                onChange={(e) => setNewReportDescription(e.target.value)}
                placeholder="Enter report description"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* データテーブル設定セクション */}
        <div className={styles.tableSettings}>
          <h4 className={styles.sectionTitle}>Table Settings</h4>

          {/* 表示列の選択 */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Display Columns ({selectedColumns.length} selected)
            </label>
            <div className={styles.columnButtons}>
              <button
                type="button"
                className={styles.selectButton}
                onClick={handleSelectAll}
              >
                Select All
              </button>
              <button
                type="button"
                className={styles.selectButton}
                onClick={handleDeselectAll}
              >
                Deselect All
              </button>
            </div>
            <div className={styles.columnList}>
              {availableColumns.map(columnName => (
                <label key={columnName} className={styles.columnItem}>
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(columnName)}
                    onChange={() => handleColumnToggle(columnName)}
                  />
                  <span>{columnName}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 最大行数の設定 */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Maximum Rows to Display</label>
            <input
              type="number"
              className={styles.formInput}
              value={maxRows}
              onChange={(e) => setMaxRows(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              max="10000"
            />
            <div className={styles.helperText}>
              Total rows in dataset: {(dataset.rowCount || 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}